import { createSelector } from '@reduxjs/toolkit';

import { sortBy } from 'es-toolkit';

import { selectExchanges } from 'src/store/shared/exchange-list/selectors';
import type { JsonExchange } from '@skynet/data-shared-exchanges';
import type { AccountsState } from '@skynet/data-shared-accounts';
import type { AutoCompleteProps } from 'antd';
import type { JsonLoan, LoansState } from './types';
import { filterAndSort, setDefaultName } from 'src/store/utils';
import type { LoanTermsState } from '@skynet/data-loan-terms';

export interface Store {
  loans: {
    index: LoansState;
    terms: LoanTermsState;
  };
  accounts: AccountsState;
}

export const selectError = (store: Store) => store.loans.index.error ?? store.loans.terms.error;
export const selectIsListenSent = (store: Store) => store.loans.index.listenStatus === 'success';

const selectLoans = (store: Store) => store.loans.index.list;

export const selectLoansList = createSelector(selectLoans, setDefaultName);

export const selectCompanies = (store: Store) => store.loans.index.companies;

export const selectIsOverviewLoading = (store: Store) => store.loans.index.isOverviewLoading;

export const selectIsNewLoanModalOpen = (store: Store) => store.loans.index.isNewLoanModalOpen;
export const selectIsLoanSubmitting = (store: Store) => store.loans.index.resNewLoan === 'pending';
export const selectIsLoanSubmitted = (store: Store) => store.loans.index.resNewLoan === 'success';

export const selectIsSidebarOpen = (store: Store) => store.loans.index.isSidebarOpen;
export const selectTargetLoan = (store: Store) => store.loans.index.targetLoan;
export const selectTargetExchange = (store: Store) => store.loans.index.targetExchange;

export const selectOverviewActiveTab = (store: Store) => store.loans.index.overviewActiveTab;

export const selectSortKey = (state: Store) => state.loans.index.sortKey;

export const selectLoanTypeFilter = (state: Store) => state.loans.index.loanTypeFilter;

export const selectHideEmptyAccounts = (state: Store) => state.loans.index.hideEmptyAccounts;

export const selectSearchInput = (state: Store) => state.loans.index.searchInput;

// company options/suggestions for autocomplete in NewLoanModal form

export const selectCompanyOptions = createSelector(selectCompanies, (items: string[]) => {
  const companyOptions: Required<AutoCompleteProps>['options'] = items.map((company: string) => ({
    key: company,
    value: company,
    label: company,
  }));

  return companyOptions;
});

// bot table with exchange details

// currentLoan could be:
//  - undefined – the initial value
//  - JsonLoan – if currentLoan is selected from the loans list
//  - null - if currentLoan could not be found in the loans list
export const selectCurrentLoan = createSelector(
  selectTargetLoan,
  selectLoansList,
  (targetLoan: string, items: JsonLoan[] | null) => {
    if (items === null || !targetLoan) {
      return undefined;
    }

    return items.find((item) => item.id === +targetLoan) ?? null;
  },
);

export const getDefaultLoanFromList = (items: JsonLoan[] | null) =>
  sortBy(items ?? [], ['id'])[0] ?? null;

export const selectDefaultLoan = createSelector(selectLoansList, (items) =>
  items ? getDefaultLoanFromList(items) : null,
);
export const selectNextLoan = createSelector(
  selectTargetLoan,
  selectLoansList,
  selectSortKey,
  (targetLoan: string, items: JsonLoan[] | null, sortKey: string) => {
    if (!items || !targetLoan) return undefined;

    const sortedItems = filterAndSort(items, sortKey);
    const currentIndex = sortedItems.findIndex((item) => item.id === +targetLoan);
    const nextIdx = currentIndex + 1;
    return nextIdx < sortedItems.length ? sortedItems[nextIdx] : undefined;
  },
);

export const selectPrevLoan = createSelector(
  selectTargetLoan,
  selectLoansList,
  selectSortKey,
  (targetLoan: string, items: JsonLoan[] | null, sortKey: string) => {
    if (!items || !targetLoan) return undefined;

    const sortedItems = filterAndSort(items, sortKey);
    const currentIndex = sortedItems.findIndex((item) => item.id === +targetLoan);
    const prevIdx = currentIndex - 1;
    return prevIdx >= 0 ? sortedItems[prevIdx] : undefined;
  },
);

export const selectCurrentExchange = createSelector(
  selectTargetExchange,
  selectExchanges,
  (targetExchange: string, items: JsonExchange[]) => {
    const currentExchange: JsonExchange | undefined = targetExchange
      ? (items.find((item) => item.id === +targetExchange) ?? undefined)
      : undefined;
    return currentExchange;
  },
);

export const selectSortedLoans = createSelector(
  selectLoansList,
  selectSortKey,
  selectSearchInput,
  (loans, sortKey, searchInput) => filterAndSort(loans ?? [], sortKey, searchInput),
);

export const selectIsLoanExist = (loanId?: string) =>
  createSelector(selectLoansList, (list) => {
    if (list === null || loanId === undefined) {
      return undefined;
    }
    return list.some((item) => item.id === Number(loanId));
  });

export const selectResHideLoanStatus = (store: Store) => store.loans.index.resHideLoan;

export const selectLoanIdAfterHide = createSelector(
  selectNextLoan,
  selectDefaultLoan,
  (nextLoan, defaultLoan) => nextLoan?.id ?? defaultLoan?.id,
);
